import _ from "lodash";
import FileProcessor from "../Processors/FileProcessor";
import useProcessingModal from "./useProcessingModal";

// Wires in the progress modal for the length of a file's processing time, and wires in the
// onProgress function for the engine handler to call to provide real time updates.
// Anytime a file is processed, a new submission object is created

const processFailureMessage = (filenames) =>
  `We couldn't process ${filenames.join(", ")}. Please check the file and try again.`;

const reprocessFailureMessage = (filenames) => `Couldn't reprocess ${filenames.join(", ")}.`;

const useProcessing = ({
  kind,
  ndcList,
  accessibleEntityIds = [],
  salt,
  fieldMappingGroups,
  onProcessed,
  onError,
  onReprocessError,
}) => {
  const { processingModalOpen, processingProgress, withProgress } = useProcessingModal();

  // One file's unit of work: what to process, plus a run() that processes it
  // data, fileId and mapping are provided for reprocess jobs
  const buildFileProcessJob = ({ fileId, filename, rawFile, data, mapping }) => ({
    fileId,
    filename,
    run: (onProgress) =>
      FileProcessor({
        file: rawFile ?? { name: filename },
        kind,
        ndcList,
        accessibleEntityIds,
        fieldMappings: fieldMappingGroups,
        salt,
        data,
        selectedMapping: mapping,
        fileId,
        onProgress,
      }).then((attachment) => {
        // engine does not mark a complete state, only progress every BATCH_SIZE, so this marks a file complete
        onProgress(1, 1);
        return attachment;
      }),
  });

  // Runs jobs behind the progress modal, providing the progressFor fn
  // Jobs run concurrently (when a mapping is updated that affects multiple files)
  const processJobs = async (jobs, onFailure) => {
    await withProgress(
      jobs.map(({ fileId, filename }) => ({ fileId, filename })),
      async (progressFor) => {
        const results = await Promise.allSettled(
          jobs.map((job) => job.run(progressFor(job.fileId))) // injects progressFor fn into engine's onProgress to keep processing modal updated
        );

        const finishedJobs = results.filter((r) => r.status === "fulfilled");
        const failedFilenames = results.flatMap((r, index) =>
          r.status === "fulfilled" ? [] : [jobs[index].filename]
        );

        if (finishedJobs.length > 0) {
          onProcessed(finishedJobs.map((r) => r.value));
        }

        if (failedFilenames.length > 0) {
          onFailure(failedFilenames);
        }
      }
    );
  };

  // on file attachment
  const handleFile = (rawFile) =>
    processJobs([buildFileProcessJob({ filename: rawFile.name, rawFile })], (filenames) =>
      onError?.(processFailureMessage(filenames))
    );

  // on column mapping change from sidebar
  const handleSelectMapping = (attachment, mappingId) => {
    const mapping = _.find(fieldMappingGroups, ["id", mappingId]);
    const job = buildFileProcessJob({
      fileId: attachment.fileId,
      filename: attachment.filename,
      data: attachment.data,
      mapping,
    });
    return processJobs([job], (filenames) => onError?.(processFailureMessage(filenames)));
  };

  // when a column mapping is updated reprocess affected files
  const reprocessAffectedFiles = async (attachments, mapping) => {
    const affected = attachments.filter((attachment) => attachment.mapping?.id === mapping.id);

    if (!affected.length) return;

    const jobs = affected.map((attachment) =>
      buildFileProcessJob({
        fileId: attachment.fileId,
        filename: attachment.filename,
        data: attachment.data,
        mapping,
      })
    );

    await processJobs(jobs, (filenames) => onReprocessError?.(reprocessFailureMessage(filenames)));
  };

  return {
    processingModalOpen,
    processingProgress,
    handleFile,
    handleSelectMapping,
    reprocessAffectedFiles,
  };
};

export default useProcessing;
